import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu } from '../components/Menu';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorNote,
  Field,
  Loading,
  Modal,
  Toggle,
  inputClass,
} from '../components/ui';
import { errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth-context-value';
import { formatRelative } from '../lib/format';
import { automationsApi } from '../lib/resources';
import type { Automation, AutomationImportReport } from '../types';

const ALL = '__all__';
const NO_GROUP = '__none__';

const CREATED_LABELS: Record<string, string> = {
  tags: 'Tags',
  additionalFields: 'Campos adicionais',
  businessLossReasons: 'Motivos de perda',
  activityTypes: 'Tipos de atividade',
  pipelines: 'Pipelines',
  stages: 'Etapas',
  departments: 'Departamentos',
  workingHours: 'Horários de trabalho',
};

function downloadJson(name: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${name.replace(/[^\p{L}\p{N}._-]+/gu, '-').toLowerCase()}.json`;
  link.click();
  URL.revokeObjectURL(url);
}

/** Automações (Fase 4 / A1): pastas, lista, ligar/desligar, importar e exportar. */
export function AutomationsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const canConfigure = can('flow_admin');
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [folder, setFolder] = useState<string>(ALL);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [removing, setRemoving] = useState<Automation | null>(null);
  const [report, setReport] = useState<AutomationImportReport | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);

  const list = useQuery({ queryKey: ['automations'], queryFn: () => automationsApi.list() });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['automations'] });

  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      automationsApi.setActive(id, active),
    onSuccess: refresh,
    onError: (error) => setPageError(errorMessage(error, 'Não foi possível ligar a automação')),
  });
  const duplicate = useMutation({
    mutationFn: (id: string) => automationsApi.duplicate(id),
    onSuccess: refresh,
    onError: (error) => setPageError(errorMessage(error)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => automationsApi.remove(id),
    onSuccess: () => {
      setRemoving(null);
      void refresh();
    },
  });
  const importBundle = useMutation({
    mutationFn: (bundle: unknown) => automationsApi.importBundle(bundle),
    onSuccess: (result) => {
      setReport(result);
      void refresh();
    },
    onError: (error) =>
      setPageError(errorMessage(error, 'Não foi possível importar o arquivo')),
  });

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setPageError(null);
    try {
      importBundle.mutate(JSON.parse(await file.text()));
    } catch {
      setPageError('O arquivo não é um JSON válido');
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const onExport = async (automation: Automation) => {
    try {
      downloadJson(automation.name, await automationsApi.exportBundle(automation.id));
    } catch (error) {
      setPageError(errorMessage(error));
    }
  };

  const all = list.data?.data ?? [];
  const groups = [...new Set(all.map((item) => item.group).filter(Boolean) as string[])].sort(
    (a, b) => a.localeCompare(b, 'pt-BR'),
  );
  const term = search.trim().toLocaleLowerCase('pt-BR');
  const visible = all.filter(
    (item) =>
      (folder === ALL || (folder === NO_GROUP ? !item.group : item.group === folder)) &&
      (!term || item.name.toLocaleLowerCase('pt-BR').includes(term)),
  );
  const countOf = (key: string) =>
    key === ALL
      ? all.length
      : key === NO_GROUP
        ? all.filter((item) => !item.group).length
        : all.filter((item) => item.group === key).length;

  const folders: Array<[string, string]> = [
    [ALL, 'Todas'],
    ...groups.map((group): [string, string] => [group, group]),
    ...(all.some((item) => !item.group) ? [[NO_GROUP, 'Sem pasta'] as [string, string]] : []),
  ];

  return (
    <div className="flex h-full min-h-0">
      <aside className="w-56 shrink-0 border-r border-slate-200 bg-white p-3">
        <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Pastas
        </p>
        <nav className="space-y-0.5">
          {folders.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFolder(key)}
              className={`flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm ${
                folder === key
                  ? 'bg-slate-100 font-medium text-slate-900'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <span className="truncate">{label}</span>
              <span className="text-xs text-slate-400">{countOf(key)}</span>
            </button>
          ))}
        </nav>
      </aside>

      <main className="min-w-0 flex-1 overflow-auto p-6">
        <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Automações</h1>
            <p className="text-sm text-slate-500">
              Fluxos que reagem a webhooks, mensagens e eventos de negócio.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              className={`${inputClass} !mt-0 w-56`}
              placeholder="Buscar automação"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <Link
              to="/configuracoes/execucoes-automacao"
              className="whitespace-nowrap text-sm text-slate-600 hover:underline"
            >
              Execuções
            </Link>
            {canConfigure && (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  accept="application/json,.json"
                  className="hidden"
                  onChange={(event) => void onFile(event.target.files?.[0])}
                />
                <Button
                  onClick={() => fileRef.current?.click()}
                  disabled={importBundle.isPending}
                >
                  {importBundle.isPending ? 'Importando…' : 'Importar'}
                </Button>
                <Button variant="primary" onClick={() => setCreating(true)}>
                  <span className="whitespace-nowrap">+ Nova automação</span>
                </Button>
              </>
            )}
          </div>
        </header>

        {pageError && (
          <div className="mb-4">
            <ErrorNote>{pageError}</ErrorNote>
          </div>
        )}

        {list.isLoading ? (
          <Loading />
        ) : list.isError ? (
          <ErrorNote>{errorMessage(list.error)}</ErrorNote>
        ) : visible.length === 0 ? (
          <EmptyState
            title={all.length ? 'Nada nesta pasta' : 'Nenhuma automação ainda'}
            hint={
              canConfigure ? 'Crie uma do zero ou importe um arquivo do DataCrazy.' : undefined
            }
          />
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Nome</th>
                  <th className="px-4 py-2 font-medium">Pasta</th>
                  <th className="px-4 py-2 font-medium">Blocos</th>
                  <th className="px-4 py-2 font-medium">Versão</th>
                  <th className="px-4 py-2 font-medium">Atualizada</th>
                  <th className="px-4 py-2 font-medium">Ligada</th>
                  <th className="w-10 px-2 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visible.map((automation) => (
                  <tr key={automation.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5">
                      <Link
                        to={`/automacoes/${automation.id}`}
                        className="font-medium text-slate-900 hover:underline"
                      >
                        {automation.name}
                      </Link>
                      {automation.description && (
                        <p className="truncate text-xs text-slate-400">
                          {automation.description}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">{automation.group ?? '—'}</td>
                    <td className="px-4 py-2.5 text-slate-600">{automation.blocks.length}</td>
                    <td className="px-4 py-2.5 text-slate-600">v{automation.version}</td>
                    <td className="px-4 py-2.5 text-slate-500">
                      {formatRelative(automation.updatedAt)}
                    </td>
                    <td className="px-4 py-2.5">
                      <Toggle
                        label={automation.active ? 'Ligada' : 'Desligada'}
                        checked={automation.active}
                        disabled={!canConfigure || toggle.isPending}
                        onChange={(active) => {
                          setPageError(null);
                          toggle.mutate({ id: automation.id, active });
                        }}
                      />
                    </td>
                    <td className="px-2 py-2.5 text-right">
                      <Menu
                        trigger={(toggle) => (
                          <button
                            type="button"
                            onClick={toggle}
                            className="rounded px-2 text-slate-400 hover:bg-slate-100"
                            aria-label="Ações da automação"
                          >
                            ⋯
                          </button>
                        )}
                        groups={[
                          {
                            items: [
                              {
                                label: 'Abrir',
                                onSelect: () => navigate(`/automacoes/${automation.id}`),
                              },
                              { label: 'Exportar', onSelect: () => void onExport(automation) },
                              ...(canConfigure
                                ? [
                                    {
                                      label: 'Duplicar',
                                      onSelect: () => duplicate.mutate(automation.id),
                                    },
                                    {
                                      label: 'Excluir',
                                      danger: true,
                                      onSelect: () => setRemoving(automation),
                                    },
                                  ]
                                : []),
                            ],
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {creating && (
        <NewAutomationModal
          groups={groups}
          initialGroup={folder !== ALL && folder !== NO_GROUP ? folder : ''}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void refresh();
          }}
        />
      )}

      {removing && (
        <ConfirmDialog
          title="Excluir automação"
          message={
            <>
              <strong>{removing.name}</strong> vai para a Lixeira desligada. Dá para restaurar
              de lá.
            </>
          }
          confirmLabel="Excluir"
          danger
          busy={remove.isPending}
          error={remove.isError ? errorMessage(remove.error) : null}
          onConfirm={() => remove.mutate(removing.id)}
          onClose={() => setRemoving(null)}
        />
      )}

      {report && <ImportReportModal report={report} onClose={() => setReport(null)} />}
    </div>
  );
}

function NewAutomationModal({
  groups,
  initialGroup,
  onClose,
  onCreated,
}: {
  groups: string[];
  initialGroup: string;
  onClose: () => void;
  onCreated: (automation: Automation) => void;
}) {
  const [name, setName] = useState('');
  const [group, setGroup] = useState(initialGroup);
  const [description, setDescription] = useState('');
  const create = useMutation({
    mutationFn: () =>
      automationsApi.create({
        name: name.trim(),
        group: group.trim() || undefined,
        description: description.trim() || undefined,
      }),
    onSuccess: onCreated,
  });

  return (
    <Modal title="Nova automação" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <Field label="Nome">
          <input
            autoFocus
            className={inputClass}
            value={name}
            maxLength={200}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field label="Pasta" hint="Escolha uma existente ou digite uma nova">
          <input
            className={inputClass}
            list="automation-groups"
            value={group}
            maxLength={120}
            onChange={(event) => setGroup(event.target.value)}
          />
          <datalist id="automation-groups">
            {groups.map((item) => (
              <option key={item} value={item} />
            ))}
          </datalist>
        </Field>
        <Field label="Descrição">
          <textarea
            className={inputClass}
            rows={3}
            value={description}
            maxLength={2000}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
        {create.isError && <ErrorNote>{errorMessage(create.error)}</ErrorNote>}
        <p className="text-xs text-slate-400">
          A automação nasce desligada, só com o bloco de gatilho.
        </p>
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={!name.trim() || create.isPending}>
            {create.isPending ? 'Criando…' : 'Criar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function ImportReportModal({
  report,
  onClose,
}: {
  report: AutomationImportReport;
  onClose: () => void;
}) {
  const created = Object.entries(report.created).filter(([, names]) => names.length > 0);
  return (
    <Modal title="Importação concluída" onClose={onClose}>
      <div className="space-y-4 text-sm text-slate-600">
        <div>
          <p className="font-medium text-slate-800">
            {report.automations.length === 1
              ? '1 automação importada, desligada:'
              : `${report.automations.length} automações importadas, desligadas:`}
          </p>
          <ul className="mt-1 list-disc pl-5">
            {report.automations.map((automation) => (
              <li key={automation.id}>
                <Link to={`/automacoes/${automation.id}`} className="hover:underline">
                  {automation.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        {created.length > 0 && (
          <div>
            <p className="font-medium text-slate-800">Criados neste workspace:</p>
            <ul className="mt-1 space-y-0.5">
              {created.map(([key, names]) => (
                <li key={key}>
                  <span className="text-slate-500">{CREATED_LABELS[key] ?? key}:</span>{' '}
                  {names.join(', ')}
                </li>
              ))}
            </ul>
          </div>
        )}
        {report.warnings.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-800">
            <p className="font-medium">Revise antes de ligar:</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              {report.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex justify-end">
          <Button variant="primary" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
